import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import MemoryIcon from "@mui/icons-material/Memory";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LinkIcon from "@mui/icons-material/Link";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { getMnemoLiveDashboard, materializeMnemoMemory } from "../api/agentApi";
import { useTheme } from "@mui/material/styles";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";

const colors = {
  black: "var(--mnemo-ink)",
  copper: "var(--mnemo-copper)",
  copperDark: "var(--mnemo-copper-text)",
  copperSoft: "var(--mnemo-copper-soft)",
  border: "var(--mnemo-border)",
  panel: "var(--mnemo-panel)",
  panelSoft: "var(--mnemo-panel-soft)",
  muted: "var(--mnemo-muted)",
  inkSoft: "var(--mnemo-ink-soft)",
  green: "var(--mnemo-green)",
  greenSoft: "var(--mnemo-green-soft)",
  blue: "var(--mnemo-blue)",
  blueSoft: "var(--mnemo-blue-soft)",
};

const actionButtonSx = {
  minHeight: 44,
  borderRadius: UI_RADIUS.button,
  px: 2.35,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 850,
  letterSpacing: 0,
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  transition: "transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease",
  "& .MuiButton-startIcon": {
    color: "inherit",
    mr: 1,
  },
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    bgcolor: "#D8C8B9",
    color: "rgba(255,255,255,0.88)",
    boxShadow: "none",
    transform: "none",
  },
};

const windows = [
  { value: 6, label: "6 hours" },
  { value: 24, label: "24 hours" },
  { value: 72, label: "3 days" },
  { value: 168, label: "7 days" },
];

function formatNumber(value) {
  const n = Number(value || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return String(n);
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${Math.round(n * 100)}%`;
}

function formatDate(value) {
  if (!value) return "Never";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function titleCase(value) {
  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase());
}

function SummaryCard({ icon, label, value, helper, tone = "copper" }) {
  const accent = tone === "blue" ? colors.blue : tone === "green" ? colors.green : colors.copper;
  const soft = tone === "blue" ? colors.blueSoft : tone === "green" ? colors.greenSoft : colors.copperSoft;
  return (
    <Box
      sx={{
        p: 2.4,
        border: `1px solid ${colors.border}`,
        borderRadius: UI_RADIUS.card,
        background: colors.panel,
        minHeight: 132,
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: BRAND_COLORS.primary.border,
          boxShadow: "0 14px 28px rgba(29, 20, 12, 0.08)",
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: soft,
          color: accent,
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 13, letterSpacing: 0, color: colors.muted, fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 34, lineHeight: 1.05, fontWeight: 900, color: colors.black, mt: 0.6 }}>
        {formatNumber(value)}
      </Typography>
      <Typography sx={{ color: colors.muted, fontSize: 14, mt: 0.6 }}>
        {helper}
      </Typography>
    </Box>
  );
}

function MemoryAppliedBadge({ ratio, runs, records, checks, considered }) {
  const subline = runs
    ? `${formatNumber(runs)} runs influenced · ${formatNumber(records)} records injected`
    : considered
      ? `0 runs influenced · ${formatNumber(considered)} records considered`
      : checks
        ? `0 runs influenced · ${formatNumber(checks)} checks observed`
        : "No memory checks yet";

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.55,
        py: 0.9,
        borderRadius: UI_RADIUS.panel,
        border: "1px solid rgba(70, 114, 65, 0.34)",
        bgcolor: colors.greenSoft,
        backgroundImage: "none",
        boxShadow: "0 2px 8px -3px rgba(70, 114, 65, 0.28), 0 10px 22px -12px rgba(70, 114, 65, 0.28)",
        minHeight: 46,
        transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(115deg, transparent 0%, transparent 34%, rgba(255,255,255,0.18) 48%, transparent 62%, transparent 100%)",
          transform: "translateX(-120%)",
          transition: "transform 620ms ease",
          pointerEvents: "none",
        },
        "&:hover": {
          borderColor: "rgba(70, 114, 65, 0.58)",
          boxShadow: "0 4px 12px -4px rgba(70, 114, 65, 0.34), 0 16px 30px -16px rgba(70, 114, 65, 0.34)",
          transform: "translateY(-1px)",
        },
        "&:hover::before": {
          transform: "translateX(120%)",
        },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: UI_RADIUS.panel,
          display: "grid",
          placeItems: "center",
          bgcolor: "rgba(70, 114, 65, 0.16)",
          color: colors.green,
          flexShrink: 0,
        }}
      >
        <MemoryIcon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={0.85} alignItems="baseline" flexWrap="wrap">
          <Typography sx={{ color: colors.black, fontWeight: 900, lineHeight: 1, fontSize: 17 }}>
            Memory Applied:
          </Typography>
          <Typography sx={{ color: colors.green, fontWeight: 950, lineHeight: 1, fontSize: 22 }}>
            {formatPercent(ratio)}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: colors.green, display: "block", fontWeight: 800, lineHeight: 1.15, opacity: 0.86, mt: 0.25 }}
        >
          {subline}
        </Typography>
      </Box>
    </Box>
  );
}

function PipelineStep({ title, value, helper, active }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 190,
        p: 2,
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${active ? BRAND_COLORS.primary.border : colors.border}`,
        bgcolor: active ? colors.copperSoft : colors.panelSoft,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Typography sx={{ fontWeight: 900, color: colors.inkSoft }}>{title}</Typography>
        <Chip
          size="small"
          label={formatNumber(value)}
          sx={{
            bgcolor: active ? colors.copperSoft : colors.panelSoft,
            color: active ? colors.copperDark : colors.muted,
            borderRadius: UI_RADIUS.chip,
            fontWeight: 900,
          }}
        />
      </Stack>
      <Typography sx={{ color: colors.muted, fontSize: 13, mt: 0.7 }}>{helper}</Typography>
    </Box>
  );
}

function ActivityCard({ title, subtitle, children }) {
  return (
    <Box
      sx={{
        p: 2.6,
        border: `1px solid ${colors.border}`,
        borderRadius: UI_RADIUS.card,
        bgcolor: colors.panel,
      }}
    >
      <Typography sx={{ fontSize: 22, fontWeight: 900, color: colors.black }}>{title}</Typography>
      <Typography sx={{ color: colors.muted, fontSize: 14, mt: 0.4, mb: 2 }}>{subtitle}</Typography>
      {children}
    </Box>
  );
}

function RecordItem({ record }) {
  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Chip size="small" label={titleCase(record.scope)} sx={{ bgcolor: colors.copperSoft, color: colors.copperDark, borderRadius: UI_RADIUS.chip, fontWeight: 900 }} />
        <Chip size="small" label={titleCase(record.category || record.memory_type)} sx={{ bgcolor: colors.panelSoft, color: colors.inkSoft, borderRadius: UI_RADIUS.chip, fontWeight: 800 }} />
        <Typography sx={{ color: colors.muted, fontSize: 13 }}>{formatDate(record.updated_at || record.created_at)}</Typography>
      </Stack>
      <Typography sx={{ color: colors.black, fontSize: 15.5, mt: 0.9, lineHeight: 1.45 }}>
        {record.content}
      </Typography>
    </Box>
  );
}

function InjectionItem({ item }) {
  return (
    <Box sx={{ py: 1.4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Box>
          <Typography sx={{ color: colors.black, fontWeight: 900 }}>
            Run {item.run_number || "pending"} · {item.status}
          </Typography>
          <Typography sx={{ color: colors.muted, fontSize: 13, mt: 0.3 }}>
            {formatDate(item.updated_at || item.created_at)}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" justifyContent="flex-end">
          <Chip size="small" label={`${formatNumber(item.records_considered)} considered`} sx={{ borderRadius: UI_RADIUS.chip }} />
          <Chip
            size="small"
            label={`${formatNumber(item.records_injected)} injected`}
            sx={{ bgcolor: item.records_injected ? colors.greenSoft : colors.panelSoft, color: item.records_injected ? colors.green : colors.muted, borderRadius: UI_RADIUS.chip, fontWeight: 900 }}
          />
        </Stack>
      </Stack>
      {item.source_scopes?.length ? (
        <Typography sx={{ color: colors.muted, fontSize: 13, mt: 0.7 }}>
          Sources: {item.source_scopes.map(titleCase).join(", ")}
        </Typography>
      ) : null}
    </Box>
  );
}

function EventItem({ event }) {
  const promoted = event.metadata?.mnemo_record_ids?.length || 0;
  return (
    <Box sx={{ py: 1.35 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" label={titleCase(event.role || "event")} sx={{ borderRadius: UI_RADIUS.chip, fontWeight: 900 }} />
          <Typography sx={{ color: colors.muted, fontSize: 13 }}>{formatDate(event.created_at)}</Typography>
        </Stack>
        <Chip
          size="small"
          label={promoted ? `${promoted} promoted` : "observed"}
          sx={{
            bgcolor: promoted ? colors.greenSoft : colors.panelSoft,
            color: promoted ? colors.green : colors.muted,
            borderRadius: UI_RADIUS.chip,
            fontWeight: 900,
          }}
        />
      </Stack>
      <Typography sx={{ color: colors.inkSoft, fontSize: 14.5, mt: 0.8, lineHeight: 1.4 }}>
        {event.content_preview || "No content captured."}
      </Typography>
    </Box>
  );
}

export default function MnemoDashboard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [materializing, setMaterializing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await getMnemoLiveDashboard(hours, 80);
      setData(next);
    } catch (err) {
      console.error(err);
      setError("Could not load Mnemo memory telemetry.");
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => data?.summary || {}, [data]);
  const recentRecords = data?.recent_records || [];
  const recentEvents = data?.recent_events || [];
  const recentInjections = data?.recent_injections || [];
  const memoryAppliedRuns = Number(summary.memory_applied_runs ?? summary.injection_runs) || 0;
  const memoryEligibleRuns = Number(summary.memory_eligible_runs ?? summary.memory_available_runs) || 0;
  const memoryChecks = Number(summary.memory_checks ?? summary.injection_runs) || 0;
  const recordsConsidered = Number(summary.records_considered) || 0;
  const memoryAppliedRecords = Number(summary.records_injected) || 0;
  const memoryNoContextRuns = Number(summary.memory_no_context_runs) || 0;
  const memoryAppliedRatio =
    summary.memory_applied_ratio !== undefined
      ? Number(summary.memory_applied_ratio) || 0
      : memoryEligibleRuns
        ? memoryAppliedRuns / memoryEligibleRuns
        : 0;

  const pipeline = useMemo(() => ([
    {
      title: "Capture",
      value: summary.events_captured,
      helper: `${formatNumber(summary.user_events)} user · ${formatNumber(summary.assistant_events)} assistant`,
    },
    {
      title: "Materialize",
      value: summary.durable_records,
      helper: `${formatNumber(summary.materialized_events)} events promoted`,
    },
    {
      title: "Evidence",
      value: summary.evidence_links,
      helper: "Source events linked to records",
    },
    {
      title: "Apply",
      value: memoryAppliedRecords,
      helper: `${formatNumber(memoryAppliedRuns)} runs influenced · ${formatNumber(recordsConsidered)} records considered`,
    },
  ]), [summary, memoryAppliedRecords, memoryAppliedRuns, recordsConsidered]);

  const handleMaterialize = async () => {
    setMaterializing(true);
    setError("");
    try {
      await materializeMnemoMemory(120);
      await load();
    } catch (err) {
      console.error(err);
      setError("Mnemo materialization failed.");
    } finally {
      setMaterializing(false);
    }
  };

  return (
    <Box
      sx={{
        pl: PAGE_SHELL.paddingLeft,
        pr: PAGE_SHELL.paddingRight,
        py: PAGE_SHELL.paddingY,
        maxWidth: PAGE_SHELL.maxWidth,
        mx: "auto",
        color: "text.primary",
        "--mnemo-ink": isDark ? "#FFF7EF" : "#090807",
        "--mnemo-ink-soft": isDark ? "#F0DFD0" : "#2A2622",
        "--mnemo-muted": isDark ? "#D9C9BA" : "#6F6860",
        "--mnemo-copper": isDark ? "#D1935C" : "#B87333",
        "--mnemo-copper-text": isDark ? "#F2C69B" : "#8A5525",
        "--mnemo-copper-soft": isDark ? "rgba(209,147,92,0.18)" : "rgba(184, 115, 51, 0.12)",
        "--mnemo-border": isDark ? "rgba(224,177,132,0.24)" : "#E7D8CA",
        "--mnemo-panel": isDark ? "#181411" : "#FFFFFF",
        "--mnemo-panel-soft": isDark ? "#211B16" : "#FBF8F5",
        "--mnemo-green": isDark ? "#91CD99" : "#467241",
        "--mnemo-green-soft": isDark ? "rgba(145,205,153,0.16)" : "#EEF7ED",
        "--mnemo-blue": isDark ? "#9EC8EF" : "#315A73",
        "--mnemo-blue-soft": isDark ? "rgba(158,200,239,0.16)" : "#EAF5FA",
      }}
    >
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2.5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
              Mnemo
            </Typography>
            <Typography variant="body2" sx={{ color: colors.muted, mt: 0.35 }}>
              Track memory capture, durable records, injection checks, and recent signals.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={1.25} flexWrap="wrap" sx={{ flexShrink: 0, pt: { xs: 0, md: 0.5 } }}>
            <MemoryAppliedBadge
              ratio={memoryAppliedRatio}
              runs={memoryAppliedRuns}
              records={memoryAppliedRecords}
              checks={memoryChecks}
              considered={recordsConsidered}
            />
            <Chip
              label="LIVE"
              size="small"
              sx={{
                bgcolor: colors.greenSoft,
                color: colors.green,
                fontWeight: 900,
                letterSpacing: 0,
                borderRadius: UI_RADIUS.chip,
                border: "1px solid rgba(70, 114, 65, 0.22)",
              }}
            />
            <Chip
              label="BETA"
              size="small"
              sx={{
                bgcolor: colors.panelSoft,
                color: colors.copperDark,
                fontWeight: 900,
                letterSpacing: 0.2,
                borderRadius: UI_RADIUS.chip,
                border: `1px solid ${BRAND_COLORS.primary.border}`,
              }}
            />
          </Stack>
        </Box>
        <Stack direction="row" alignItems="center" gap={1.2} useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          <Select
            size="small"
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            sx={{
              minWidth: 136,
              borderRadius: UI_RADIUS.input,
              bgcolor: colors.panelSoft,
              ".MuiOutlinedInput-notchedOutline": { borderColor: BRAND_COLORS.primary.border },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D8C9BB" },
            }}
          >
            {windows.map(window => (
              <MenuItem key={window.value} value={window.value}>{window.label}</MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            disableElevation
            aria-label="Refresh Mnemo telemetry"
            onClick={load}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <RefreshIcon fontSize="small" />}
            sx={actionButtonSx}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleMaterialize}
            disabled={materializing}
            startIcon={materializing ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <AutoAwesomeIcon />}
            sx={actionButtonSx}
          >
            Materialize
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: PAGE_SHELL.sectionGap }}>{error}</Alert> : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: PAGE_SHELL.sectionGap }}>
        <SummaryCard icon={<MemoryIcon />} label="Events Captured" value={summary.events_captured} helper="Recent conversation signals" tone="blue" />
        <SummaryCard icon={<AutoAwesomeIcon />} label="Durable Memories" value={summary.durable_records} helper="Active promoted records" />
        <SummaryCard icon={<LinkIcon />} label="Evidence Links" value={summary.evidence_links} helper="Records tied to source events" tone="green" />
        <SummaryCard
          icon={<ShieldOutlinedIcon />}
          label="Memory Checks"
          value={memoryChecks}
          helper={`${formatNumber(memoryEligibleRuns)} eligible · ${formatNumber(memoryNoContextRuns)} no context · ${formatNumber(memoryAppliedRecords)} injected`}
        />
      </Box>

      <Box sx={{ p: 2, border: `1px solid ${colors.border}`, borderRadius: UI_RADIUS.card, bgcolor: colors.panel, mb: PAGE_SHELL.sectionGap }}>
        <Stack direction={{ xs: "column", lg: "row" }} gap={1.4}>
          {pipeline.map(step => (
            <PipelineStep key={step.title} {...step} active={Number(step.value || 0) > 0} />
          ))}
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
          <CircularProgress sx={{ color: colors.copper }} />
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.15fr 1fr" }, gap: 2.2 }}>
          <ActivityCard title="Durable Memory" subtitle="Promoted records that can be injected into future runs.">
            {recentRecords.length ? recentRecords.slice(0, 8).map((record, index) => (
              <React.Fragment key={record.id}>
                <RecordItem record={record} />
                {index < Math.min(recentRecords.length, 8) - 1 ? <Divider /> : null}
              </React.Fragment>
            )) : (
              <Typography sx={{ color: colors.muted }}>No durable memories promoted yet.</Typography>
            )}
          </ActivityCard>

          <ActivityCard title="Memory Application Health" subtitle="Recent model calls where Mnemo context was checked or injected.">
            {recentInjections.length ? recentInjections.slice(0, 8).map((item, index) => (
              <React.Fragment key={item.message_id}>
                <InjectionItem item={item} />
                {index < Math.min(recentInjections.length, 8) - 1 ? <Divider /> : null}
              </React.Fragment>
            )) : (
              <Typography sx={{ color: colors.muted }}>No memory checks observed yet.</Typography>
            )}
          </ActivityCard>

          <ActivityCard title="Scope Mix" subtitle="Where active memory currently lives.">
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.4 }}>
              {["session", "user", "agent", "enterprise"].map(scope => (
                <Box key={scope} sx={{ p: 1.7, borderRadius: UI_RADIUS.card, bgcolor: colors.panelSoft, border: `1px solid ${colors.border}` }}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <AccountTreeIcon sx={{ color: colors.copper, fontSize: 19 }} />
                    <Typography sx={{ fontWeight: 900 }}>{titleCase(scope)}</Typography>
                  </Stack>
                  <Typography sx={{ color: colors.black, fontWeight: 900, fontSize: 28, mt: 0.8 }}>
                    {formatNumber(data?.records_by_scope?.[scope] || 0)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </ActivityCard>

          <ActivityCard title="Recent Signals" subtitle="Conversation events observed by Mnemo before promotion.">
            {recentEvents.length ? recentEvents.slice(0, 8).map((event, index) => (
              <React.Fragment key={event.id}>
                <EventItem event={event} />
                {index < Math.min(recentEvents.length, 8) - 1 ? <Divider /> : null}
              </React.Fragment>
            )) : (
              <Typography sx={{ color: colors.muted }}>No memory events captured in this window.</Typography>
            )}
          </ActivityCard>
        </Box>
      )}
    </Box>
  );
}
